import React from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { Box, Card, CardContent, Chip, IconButton, Typography, Tooltip, Avatar, Stack } from "@mui/material";
import { DragIndicator, Delete } from "@mui/icons-material";

const SortableSessionItem = ({ session, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: session.session_id,
  });

  // Describe how the session is parented in the content tree. The picker
  // filter only admits sessions with a category or non-book subcategory, but
  // the pivot can already contain orphan / challenge / series / audiobook
  // sessions inherited from the old session_is_default_recommended boolean.
  // Surface that context so admins can see and curate them.
  const parentLabel = (() => {
    if (session.session_is_chapter || session.subcategory_is_book) {
      return session.subcategory_title ? `Audiobook: ${session.subcategory_title}` : "Audiobook";
    }
    if (session.challenge_title) {
      return `Challenge: ${session.challenge_title}`;
    }
    if (session.series_title) {
      return `Series: ${session.series_title}`;
    }
    if (session.subcategory_title) return session.subcategory_title;
    if (session.category_title) return session.category_title;
    return "No Category";
  })();
  const subtext = `ID#${session.session_id} · ${parentLabel}`;

  // Per-frame drag values stay on the inline `style` prop to avoid
  // Emotion regenerating an `sx` class on every mousemove during drag.
  // Matches the pattern used by SortableChallenge / SortableItemList /
  // AddEditChallenges' SortableSessions.
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        display: "flex",
        alignItems: "center",
        boxShadow: isDragging ? 4 : 1,
        backgroundColor: "white",
      }}
      {...attributes}
    >
      <Box
        {...listeners}
        sx={{
          cursor: "grab",
          display: "flex",
          alignItems: "center",
          px: 1,
          color: "text.secondary",
          "&:active": { cursor: "grabbing" },
        }}
      >
        <DragIndicator />
      </Box>
      <Typography variant="body2" sx={{ width: 36, textAlign: "center", color: "text.secondary", fontWeight: 600 }}>
        {index + 1}
      </Typography>
      <Avatar
        src={session.session_image || undefined}
        alt={session.session_name}
        variant="rounded"
        slotProps={{ img: { loading: "lazy" } }}
        sx={{ width: 48, height: 48, mr: 1.5, ml: 0.5, bgcolor: "grey.200" }}
      />
      <CardContent
        sx={{
          flexGrow: 1,
          py: 1,
          "&:last-child": { pb: 1 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {session.session_name}
          </Typography>
          <Chip
            label={session.session_is_free ? "Free" : "Premium"}
            color={session.session_is_free ? "success" : "warning"}
            size="small"
            variant="outlined"
          />
          {!session.session_is_active && (
            <Chip label="Inactive" color="default" size="small" variant="outlined" />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {subtext}
        </Typography>
      </CardContent>
      <Tooltip title="Remove from list">
        <IconButton onClick={() => onRemove(session.session_id)} size="small" sx={{ mr: 1 }}>
          <Delete fontSize="small" />
        </IconButton>
      </Tooltip>
    </Card>
  );
};

const SortableSessionList = ({ items, onReorder, onRemove }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = event => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex(s => s.session_id === active.id);
    const newIndex = items.findIndex(s => s.session_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(oldIndex, newIndex);
  };

  if (!items || items.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">No sessions in this list yet. Use the panel on the right to add some.</Typography>
      </Box>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    >
      <SortableContext items={items.map(s => s.session_id)} strategy={verticalListSortingStrategy}>
        <Stack spacing={0} sx={{ p: 1 }}>
          {items.map((session, index) => (
            <SortableSessionItem key={session.session_id} session={session} index={index} onRemove={onRemove} />
          ))}
        </Stack>
      </SortableContext>
    </DndContext>
  );
};

export default SortableSessionList;
